import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiBarChart2, FiDollarSign, FiTrendingUp, FiShield, FiMessageSquare, FiTarget } from '../utils/icons';
import Layout from '../components/layout/Layout';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import mascot from '../assets/placeholder.png';
import { useUserData } from '../utils/UserDataContext';

const FinancialIntelligence: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const { userData } = useUserData();

  // Get personalized insights from user data
  const spendingInsights = userData.aiInsights.spendingInsights;
  const recommendations = userData.aiInsights.recommendations;
  const riskProfile = userData.aiInsights.riskProfile;

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Financial Intelligence Hub</h1>
            <p className="text-text/70 max-w-3xl">
              Access AI-powered insights and analytics to make smarter financial decisions. Our system analyzes your financial data to provide personalized recommendations.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex overflow-x-auto gap-2 mb-8 pb-2 -mx-4 px-4 md:mx-0 md:px-0">
            <Button 
              variant={activeTab === 'overview' ? 'primary' : 'ghost'} 
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </Button>
            <Button 
              variant={activeTab === 'insights' ? 'primary' : 'ghost'} 
              onClick={() => setActiveTab('insights')}
            >
              AI Insights
            </Button>
            <Button 
              variant={activeTab === 'services' ? 'primary' : 'ghost'} 
              onClick={() => setActiveTab('services')}
            >
              Services
            </Button>
            <Button 
              variant={activeTab === 'chatbot' ? 'primary' : 'ghost'} 
              onClick={() => setActiveTab('chatbot')}
            >
              AI Assistant
            </Button>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card variant="glass" className="p-6">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <FiBarChart2 className="text-primary" size={24} />
                  </div>
                  <h2 className="text-xl font-semibold mb-2">AI-Powered Analytics</h2>
                  <p className="text-text/70 mb-4">Get advanced insights from your financial data with our machine learning algorithms.</p>
                  <Link to="/consumption-insights">
                    <Button variant="ghost" rightIcon={<FiArrowRight size={16} />}>
                      View Analytics
                    </Button>
                  </Link>
                </Card>

                <Card variant="glass" className="p-6">
                  <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center mb-4">
                    <FiTrendingUp className="text-secondary" size={24} />
                  </div>
                  <h2 className="text-xl font-semibold mb-2">Risk Assessment</h2>
                  <p className="text-text/70 mb-4">Understand your financial risk profile with our AI risk assessment tools.</p>
                  <Link to="/risk-assessment">
                    <Button variant="ghost" rightIcon={<FiArrowRight size={16} />}>
                      Check Your Risk
                    </Button>
                  </Link>
                </Card>

                <Card variant="glass" className="p-6">
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mb-4">
                    <FiShield className="text-accent" size={24} />
                  </div>
                  <h2 className="text-xl font-semibold mb-2">Fraud Detection</h2>
                  <p className="text-text/70 mb-4">Our AI continuously monitors your accounts to detect and prevent fraudulent activities.</p>
                  <Link to="/fraud-detection">
                    <Button variant="ghost" rightIcon={<FiArrowRight size={16} />}>
                      View Security
                    </Button>
                  </Link>
                </Card>
              </section>

              <section>
                <Card variant="glass" className="p-6">
                  <div className="flex items-start gap-6 flex-col md:flex-row">
                    <div className="w-full md:w-3/5">
                      <h2 className="text-2xl font-semibold mb-4">Your Financial Assistant</h2>
                      <p className="text-text/70 mb-6">
                        Our AI-powered financial assistant is available 24/7 to help you with any financial questions, provide advice, and guide you through complex financial decisions.
                      </p>
                      <div className="flex flex-wrap gap-4">
                        <Link to="/chat">
                          <Button rightIcon={<FiMessageSquare size={16} />}>
                            Chat with AI
                          </Button>
                        </Link>
                        <Link to="/dashboard">
                          <Button variant="outline" rightIcon={<FiArrowRight size={16} />}>
                            View Dashboard
                          </Button>
                        </Link>
                      </div>
                    </div>
                    <div className="w-full md:w-2/5 h-64 flex items-center justify-center bg-foreground rounded-lg">
                      <img src={mascot} alt="AI Assistant" className="w-32 h-32 object-contain" />
                    </div>
                  </div>
                </Card>
              </section>
            </div>
          )}

          {activeTab === 'insights' && (
            <div className="space-y-6">
              <section>
                <Card variant="glass" className="p-6">
                  <h2 className="text-2xl font-semibold mb-6">Personalized Insights</h2>
                  
                  <div className="space-y-6">
                    {spendingInsights.map(insight => (
                      <div key={insight.id} className="p-4 border border-border rounded-lg bg-card/60">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <FiDollarSign className="text-primary" size={20} />
                          </div>
                          <div>
                            <h3 className="font-semibold">{insight.category} Spending Pattern</h3>
                            <p className="text-sm text-text/70">
                              {Math.abs(insight.comparison.percentChange) > 10 ? 'High' : 'Medium'} confidence ({Math.abs(insight.comparison.percentChange).toFixed(1)}%)
                            </p>
                          </div>
                        </div>
                        <p className="mb-4">{insight.description}</p>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline">Learn More</Button>
                          <Button size="sm">Get Recommendations</Button>
                        </div>
                      </div>
                    ))}
                    
                    {recommendations.map(recommendation => (
                      <div key={recommendation.id} className="p-4 border border-border rounded-lg bg-card/60">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                            <FiTarget className="text-accent" size={20} />
                          </div>
                          <div>
                            <h3 className="font-semibold">{recommendation.title}</h3>
                            <p className="text-sm text-text/70">{recommendation.category} • {recommendation.priority} priority</p>
                          </div>
                        </div>
                        <p className="mb-4">{recommendation.description}</p>
                        {recommendation.potentialSavings && (
                          <div className="mb-4 p-2 bg-accent/10 rounded text-sm">
                            Potential annual savings: ${recommendation.potentialSavings.toLocaleString()}
                          </div>
                        )}
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline">Show Details</Button>
                          <Button 
                            size="sm"
                            disabled={recommendation.implementationStatus !== 'pending'}
                          >
                            {recommendation.implementationStatus === 'pending' 
                              ? 'Apply Suggestion' 
                              : recommendation.implementationStatus === 'in_progress'
                                ? 'In Progress'
                                : 'Completed'
                            }
                          </Button>
                        </div>
                      </div>
                    ))}
                    
                    {spendingInsights.length === 0 && recommendations.length === 0 && (
                      <div className="text-center py-8">
                        <p className="text-text/50">No insights available yet. Continue using the platform to generate personalized insights.</p>
                      </div>
                    )}
                  </div>
                </Card>
              </section>
            </div>
          )}

          {activeTab === 'services' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card variant="glass" className="p-6">
                <h3 className="text-xl font-semibold mb-4">Personalized Financial Advice</h3>
                <p className="text-text/70 mb-6">Receive customized financial guidance based on your unique financial situation and goals.</p>
                <Link to="/chat">
                  <Button variant="outline" fullWidth>Access Advisor</Button>
                </Link>
              </Card>
              
              <Card variant="glass" className="p-6">
                <h3 className="text-xl font-semibold mb-4">Automated Compliance Management</h3>
                <p className="text-text/70 mb-6">Let our AI ensure your financial activities comply with relevant regulations and best practices.</p>
                <Button variant="outline" fullWidth>Check Compliance</Button>
              </Card>
              
              <Card variant="glass" className="p-6">
                <h3 className="text-xl font-semibold mb-4">Credit Assessment</h3>
                <p className="text-text/70 mb-6">Get AI-powered analysis of your credit profile with actionable steps to improve your score.</p>
                <Button variant="outline" fullWidth>View Credit Analysis</Button>
              </Card>
              
              <Card variant="glass" className="p-6">
                <h3 className="text-xl font-semibold mb-4">Loan Decision Making</h3>
                <p className="text-text/70 mb-6">Our AI evaluates loan options to help you make informed decisions on borrowing.</p>
                <Button variant="outline" fullWidth>Explore Loan Options</Button>
              </Card>
              
              <Card variant="glass" className="p-6">
                <h3 className="text-xl font-semibold mb-4">Customer Support</h3>
                <p className="text-text/70 mb-6">Get instant help from our AI chatbot for any questions or issues with your account.</p>
                <Link to="/chat">
                  <Button variant="outline" fullWidth>Contact Support</Button>
                </Link>
              </Card>
              
              <Card variant="glass" className="p-6">
                <h3 className="text-xl font-semibold mb-4">Financial Education</h3>
                <p className="text-text/70 mb-6">Access personalized learning resources to improve your financial literacy.</p>
                <Button variant="outline" fullWidth>Start Learning</Button>
              </Card>
            </div>
          )}

          {activeTab === 'chatbot' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card variant="glass" className="p-6 h-[500px] flex flex-col">
                  <div className="flex items-center gap-4 mb-6 pb-4 border-b border-border">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary via-secondary to-accent p-0.5">
                      <div className="w-full h-full rounded-full bg-card flex items-center justify-center">
                        <img src={mascot} alt="AI Assistant" className="w-6 h-6 object-contain" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold">Matrix Finance AI</h3>
                      <p className="text-sm text-text/70">Your personal finance assistant</p>
                    </div>
                  </div>
                  
                  <div className="flex-grow overflow-y-auto mb-4 space-y-4">
                    <div className="flex justify-end mb-4">
                      <div className="bg-primary/10 text-text rounded-tl-xl rounded-tr-xl rounded-bl-xl p-4 max-w-[80%]">
                        <p>How can I improve my savings?</p>
                      </div>
                    </div>
                    
                    <div className="flex mb-4">
                      <div className="bg-card rounded-tl-xl rounded-tr-xl rounded-br-xl p-4 max-w-[80%] shadow-sm">
                        <p className="mb-3">Based on your current financial data, here are 3 personalized ways to improve your savings:</p>
                        <ol className="list-decimal pl-5 space-y-2">
                          <li>Set up automatic transfers of $200 to your savings account on each payday</li>
                          <li>Reduce subscription services by consolidating or eliminating unused ones ($85/month savings potential)</li>
                          <li>Apply the 24-hour rule before making non-essential purchases over $100</li>
                        </ol>
                        <p className="mt-3">Would you like me to help you implement any of these suggestions?</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-auto">
                    <div className="flex gap-2">
                      <input 
                        type="text" 
                        className="flex-grow rounded-lg border border-border bg-card px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Ask any financial question..."
                        disabled
                      />
                      <Button disabled>
                        <FiArrowRight size={18} />
                      </Button>
                    </div>
                    <p className="text-center mt-2 text-sm text-text/50">
                      For full chat functionality, <Link to="/chat" className="text-primary hover:underline">go to Chat page</Link>
                    </p>
                  </div>
                </Card>
              </div>
              
              <div>
                <Card variant="glass" className="p-6">
                  <h3 className="text-xl font-semibold mb-4">AI Assistant Features</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                        <FiMessageSquare className="text-primary" size={14} />
                      </div>
                      <div>
                        <h4 className="font-medium">24/7 Availability</h4>
                        <p className="text-sm text-text/70">Get answers to your financial questions anytime, day or night.</p>
                      </div>
                    </li>
                    
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-secondary/10 flex items-center justify-center mt-0.5">
                        <FiBarChart2 className="text-secondary" size={14} />
                      </div>
                      <div>
                        <h4 className="font-medium">Data-Driven Insights</h4>
                        <p className="text-sm text-text/70">Receive personalized advice based on your financial data and behavior patterns.</p>
                      </div>
                    </li>
                    
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center mt-0.5">
                        <FiTarget className="text-accent" size={14} />
                      </div>
                      <div>
                        <h4 className="font-medium">Goal Setting & Tracking</h4>
                        <p className="text-sm text-text/70">Set financial goals and get AI assistance to stay on track and achieve them.</p>
                      </div>
                    </li>
                  </ul>
                  
                  <div className="mt-6">
                    <Link to="/chat">
                      <Button fullWidth>
                        Start Chatting with AI
                      </Button>
                    </Link>
                  </div>
                </Card>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default FinancialIntelligence; 